import { isValid } from 'date-fns/isValid';
import React, { useCallback, useState } from 'react';

import { getZonedDate, ZonedDate } from '@eika-infrastruktur/date';
import { Button } from '@eika/button';
import { DateInput } from '@eika/date-input';

import useTranslation from '../../../hooks/useTranslation';
import { EikaModal } from '../../EikaModal';
import { SelectPanelList } from '../../SelectPanelList';
import { StickyFooterRow } from '../StickyFooter';
import { EndType, endTypes } from './EndDateType';
import { clonedZonedDateToDate } from './utils';
import { getEndDate, getEndDateType } from './utils/getEndDateAndType';

interface Props {
    onChange: (endDate?: Date) => void;
    minDate?: Date;
    date?: Date;
    visible: boolean;
    setVisible: React.Dispatch<boolean>;
}

const EndDateField: React.FC<Props> = ({ onChange, date, visible, setVisible, minDate }) => {
    const translated = useTranslation();
    const [isCompleted, setCompleted] = useState(false);
    const [endType, setEndType] = useState<EndType>(getEndDateType(date));
    const [openCustomDateSelect, setOpenCustomDateSelect] = useState(false);
    const timeout = isCompleted ? 400 : undefined;

    const handleCalendarSelect = (zonedDate?: ZonedDate) => {
        const dateClone = clonedZonedDateToDate(zonedDate);
        onChange(isValid(dateClone) ? dateClone : undefined);
    };

    const closeAndSelectEndDate = useCallback(
        (value: EndType) => {
            const targetDate = getEndDate(value);
            onChange(targetDate);
            setOpenCustomDateSelect(false);
            setCompleted(true);
        },
        [onChange, setOpenCustomDateSelect],
    );

    return (
        <>
            <EikaModal
                className="payment-enddate__modal"
                id="paymentEnddateModal"
                open={visible}
                setOpen={setVisible}
                onClose={() => {
                    setCompleted(false);
                    setOpenCustomDateSelect(false);
                }}
                title={translated.dateField.paymentInterval.titleEnd}
                timeOut={timeout}
            >
                <SelectPanelList id="payment-interval-selector">
                    {endTypes.map((end) => (
                        <SelectPanelList.Item<EndType>
                            key={end}
                            value={end}
                            check={endType === end}
                            onClick={(value: EndType) => {
                                setEndType(value);
                                if (value === 'CUSTOM') {
                                    setOpenCustomDateSelect(true);
                                } else {
                                    closeAndSelectEndDate(value);
                                }
                            }}
                        >
                            <p className="select-panel-item__end-date">{translated.dateField.endDates[end]}</p>
                        </SelectPanelList.Item>
                    ))}
                </SelectPanelList>
                <EikaModal
                    className="payment-enddate__modal"
                    id="paymentEnddateModal-custom"
                    open={openCustomDateSelect}
                    setOpen={setOpenCustomDateSelect}
                    title={translated.dateField.paymentInterval.titleEnd}
                >
                    <section className="payment-enddate__custom-input--wrapper">
                        <DateInput
                            onChange={handleCalendarSelect}
                            date={getZonedDate(date)}
                            minDate={minDate ? getZonedDate(minDate) : undefined}
                            maxDate={undefined}
                            data-focus-first
                            label={translated.date}
                        />
                    </section>
                    <StickyFooterRow>
                        <Button onClick={() => setVisible(false)}>{translated.save}</Button>
                    </StickyFooterRow>
                </EikaModal>
            </EikaModal>
        </>
    );
};

export default EndDateField;
